import { getPredictionStatus } from './predictor.js';
import {
  compareUgmacCollegePreference,
  compareUgmacCoursePreference,
  filterUgmacCutoffRows,
  getUgmacCollegePreferenceRank,
  getUgmacCoursePreferenceRank,
} from './ugmacData.js';

const statusSortOrder = {
  'LOW CHANCE': 0,
  BORDERLINE: 1,
  SAFE: 2,
  'NO CHANCE': 3,
};

const getRelevantRankFields = (row) => {
  const cat = row.category;
  if (cat === 'UR' || cat === 'F_UR' || cat === 'RCG') {
    return {
      openingRank: cat === 'UR' ? (row.urOpeningRank ?? row.neetOpeningRank) : row.categoryOpeningRank,
      closingRank: cat === 'UR' ? (row.urClosingRank ?? row.neetClosingRank) : row.categoryClosingRank,
      rankType: 'General Rank',
    };
  }

  return {
    openingRank: row.categoryOpeningRank,
    closingRank: row.categoryClosingRank,
    rankType: 'Category Rank',
  };
};

export const predictUgmacColleges = ({
  roundKey = 'round3',
  neetRank,
  generalRank,
  categoryRank,
  category,
  gender,
  rcgRank,
  femaleGeneralRank,
  femaleCategoryRank,
  instituteType = 'All',
  institution = 'All',
  course = 'All',
}) => {
  const numericNeetRank = Number(neetRank);
  const numericGeneralRank = Number(generalRank);
  const numericCategoryRank = Number(categoryRank);
  const seatType = gender === 'Female' ? 'All' : 'General';

  if (
    !numericNeetRank ||
    numericNeetRank <= 0 ||
    !numericGeneralRank ||
    numericGeneralRank <= 0 ||
    (category !== 'UR' && category !== 'All' && (!numericCategoryRank || numericCategoryRank <= 0))
  ) {
    return [];
  }

  const rows = filterUgmacCutoffRows({
    roundKey,
    instituteType,
    institution,
    course,
    seatType,
    category: 'All',
  }).filter((row) => {
    const cat = row.category;
    const isRcgCat = cat === 'RCG';

    if (gender === 'Female') {
      if (category === 'All') {
        return true;
      }
      const canCompeteForRcg = category !== 'UR' && category !== 'EWS';
      if (isRcgCat) {
        return canCompeteForRcg;
      }
      if (category === 'UR') {
        return cat === 'UR' || cat === 'F_UR';
      }
      return (
        cat === 'UR' ||
        cat === 'F_UR' ||
        cat === category ||
        cat === `F_${category}`
      );
    } else {
      if (category === 'All') {
        return true;
      }
      if (category === 'UR') {
        return cat === 'UR';
      }
      return cat === 'UR' || cat === category;
    }
  });

  return rows
    .map((row) => {
      const { openingRank, closingRank, rankType } = getRelevantRankFields(row);
      const cat = row.category;
      
      let rankUsed = Number.POSITIVE_INFINITY;
      if (gender === 'Female') {
        const isFemaleSeat = row.seatType === 'Female';
        const isRCG = cat === 'RCG';
        const isFemaleSpecific = isFemaleSeat || isRCG;

        if (isFemaleSpecific) {
          if (cat === 'UR' || cat === 'F_UR') {
            rankUsed = Number(femaleGeneralRank) || numericGeneralRank || Number.POSITIVE_INFINITY;
          } else if (cat === 'RCG') {
            rankUsed = Number(rcgRank) || Number(femaleGeneralRank) || numericGeneralRank || Number.POSITIVE_INFINITY;
          } else if (category === 'All' || cat === category || cat === `F_${category}`) {
            rankUsed = Number(femaleCategoryRank) || numericCategoryRank || Number.POSITIVE_INFINITY;
          }
        } else {
          if (cat === 'UR' || cat === 'F_UR') {
            rankUsed = numericGeneralRank;
          } else if (cat === 'RCG') {
            rankUsed = Number(rcgRank) || numericGeneralRank;
          } else if (category === 'All' || cat === category) {
            rankUsed = numericCategoryRank || Number.POSITIVE_INFINITY;
          }
        }
      } else {
        if (cat === 'UR' || cat === 'F_UR') {
          rankUsed = numericGeneralRank;
        } else if (category === 'All' || cat === category) {
          rankUsed = numericCategoryRank || Number.POSITIVE_INFINITY;
        }
      }

      return {
        ...row,
        openingRankUsed: openingRank,
        closingRankUsed: closingRank,
        rankType,
        rankUsed,
        status: getPredictionStatus({ rankUsed, closingRank }),
        gap: Number.isFinite(rankUsed) && closingRank ? closingRank - rankUsed : Number.NEGATIVE_INFINITY,
        collegePreferenceRank: getUgmacCollegePreferenceRank(row.college),
        coursePreferenceRank: getUgmacCoursePreferenceRank(row.course),
      };
    })
    .filter((row) => Number.isFinite(row.closingRankUsed))
    .sort(
      (first, second) =>
        (statusSortOrder[first.status] ?? 99) - (statusSortOrder[second.status] ?? 99) ||
        (first.closingRankUsed ?? Number.MAX_SAFE_INTEGER) -
          (second.closingRankUsed ?? Number.MAX_SAFE_INTEGER) ||
        compareUgmacCollegePreference(first.college, second.college) ||
        compareUgmacCoursePreference(first.course, second.course) ||
        first.seatType.localeCompare(second.seatType) ||
        first.category.localeCompare(second.category),
    );
};
